
import { GoogleGenAI, Type, GenerateContentResponse } from "@google/genai";
import { FileData, AssessmentResult, RubricAnalysis } from "../types";

export const BASE_SYSTEM_INSTRUCTION = `
You are a Senior Academic Lead in Pharmacy and Pharmaceutical Sciences at the University of Nottingham (UK).
Your specialty includes Clinical Pharmacy, Pharmaceutics, Pharmacology, and Pharmacy Law/Ethics.

CRITICAL ROLE:
Provide DRAFT marks and feedback for undergraduate student work (MPharm/PharmSci).
Adhere strictly to the General Pharmaceutical Council (GPhC) Standards for Initial Education and Training.

MARKING PHILOSOPHY:
- Strictly follow the PROVIDED RUBRIC.
- RUBRIC INTERPRETATION: The rubric may be a text extraction from a table or a structured JSON object. If JSON, map 'criteria' to 'criteriaResults'. The 'marks' field in JSON represents the maximum marks for that criterion.
- VISUAL CONTENT HANDLING: You are reading a text-only extraction. If a criterion specifically mentions "Figures", "Diagrams", "Chemical Structures", or "Images", mark it as isVisual: true. Provide a neutral/mid-range "draft" mark for it, but clearly state in the feedback that visual evidence could not be verified.
- Prioritize Patient Safety: If a student makes a clinical error that would cause patient harm, reflect this in the 'Areas for Improvement'.
- Use UK English (e.g., 'paracetamol', 'community pharmacy').

MARKING SCALES (NOTTINGHAM UK):
- 70%+: First Class (Exceptional reasoning, evidence of independent reading)
- 60-69%: Upper Second (2:1 - Very good, sound knowledge)
- 50-59%: Lower Second (2:2 - Good, covers basics well)
- 40-49%: Third Class (Satisfactory, borderline pass)
- 30-39%: Compensatable Fail
- <30%: Clear Fail

Output MUST be valid JSON according to the schema provided.
`;

const ASSESSMENT_SCHEMA = {
  type: Type.OBJECT,
  properties: {
    overallMark: { type: Type.NUMBER, description: "Final percentage mark (0-100) including all criteria." },
    minMark: { type: Type.NUMBER, description: "Lower bound of draft range." },
    maxMark: { type: Type.NUMBER, description: "Upper bound of draft range." },
    classification: { type: Type.STRING, description: "UK Degree classification" },
    criteriaResults: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          criterion: { type: Type.STRING },
          mark: { type: Type.NUMBER },
          maxMark: { type: Type.NUMBER },
          feedback: { type: Type.STRING },
          isVisual: { type: Type.BOOLEAN, description: "True if this criterion requires visual inspection (figures/diagrams) which AI cannot see." }
        },
        required: ["criterion", "mark", "maxMark", "feedback"]
      }
    },
    generalStrengths: { type: Type.ARRAY, items: { type: Type.STRING } },
    areasForImprovement: { type: Type.ARRAY, items: { type: Type.STRING } },
    summaryFeedback: { type: Type.STRING }
  },
  required: ["overallMark", "minMark", "maxMark", "classification", "criteriaResults", "generalStrengths", "areasForImprovement", "summaryFeedback"]
};

const ensureArray = (val: any): string[] => {
  if (Array.isArray(val)) return val.map(v => String(v));
  if (typeof val === 'string' && val.trim()) return [val];
  return [];
};

export const assessAssignment = async (
  rubric: FileData,
  assignment: FileData,
  modelName: string = 'gemini-3-pro-preview',
  extraContext: string = ''
): Promise<AssessmentResult> => {
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  const finalInstruction = `
    ${BASE_SYSTEM_INSTRUCTION}
    Today's date is ${today}. 
    IMPORTANT: The current date is ${today}. When checking references, only flag them as "future dated" if they are dated AFTER today's date.
    
    ${extraContext ? `SPECIFIC MODULE CONTEXT:\n${extraContext}\n` : ''}
    Note: Always provide a complete JSON response. If criteria rely on images, flag them as isVisual: true.
  `;

  const response: GenerateContentResponse = await ai.models.generateContent({
    model: modelName,
    contents: {
      parts: [
        { text: "Rubric Context:" },
        { inlineData: { data: rubric.base64, mimeType: rubric.mimeType } },
        { text: "Student Submission (Draft):" },
        { inlineData: { data: assignment.base64, mimeType: assignment.mimeType } }
      ]
    },
    config: {
      systemInstruction: finalInstruction,
      responseMimeType: "application/json",
      responseSchema: ASSESSMENT_SCHEMA,
      temperature: 0.1,
    }
  });

  const rawJson = JSON.parse(response.text || "{}");
  
  // Robust normalization to prevent runtime errors
  const criteriaResults = Array.isArray(rawJson.criteriaResults) ? rawJson.criteriaResults : [];
  
  // Calculate raw totals and percentage
  const totalEarned = criteriaResults.reduce((sum: number, c: any) => sum + (Number(c.mark) || 0), 0);
  let totalMax = criteriaResults.reduce((sum: number, c: any) => sum + (Number(c.maxMark) || 0), 0);
  
  // Fallback if maxMark is only provided at the top level
  if (totalMax === 0 && rawJson.maxMark) {
    totalMax = Number(rawJson.maxMark);
  }
  
  const overallMark = totalMax > 0 ? Math.round((totalEarned / totalMax) * 100) : (Number(rawJson.overallMark) || 0);

  const result: AssessmentResult = {
    ...rawJson,
    fileName: assignment.name,
    criteriaResults,
    totalEarned,
    totalMax,
    overallMark,
    generalStrengths: ensureArray(rawJson.generalStrengths),
    areasForImprovement: ensureArray(rawJson.areasForImprovement)
  };
  
  // Recalculate Adjusted Mark excluding visual criteria
  const nonVisual = result.criteriaResults.filter(c => !c.isVisual);
  if (nonVisual.length > 0 && nonVisual.length < result.criteriaResults.length) {
    const earned = nonVisual.reduce((sum, c) => sum + (Number(c.mark) || 0), 0);
    const totalPossible = nonVisual.reduce((sum, c) => sum + (Number(c.maxMark) || 0), 0);
    result.adjustedMark = totalPossible > 0 ? Math.round((earned / totalPossible) * 100) : result.overallMark;
  } else {
    result.adjustedMark = result.overallMark;
  }

  return result;
};

export const analyzeRubric = async (
  rubric: FileData
): Promise<RubricAnalysis> => {
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

  const response: GenerateContentResponse = await ai.models.generateContent({
    model: 'gemini-3-pro-preview',
    contents: {
      parts: [
        { text: "Evaluate this Pharmacy rubric for MPharm/PharmSci suitability and clarity:" },
        { inlineData: { data: rubric.base64, mimeType: rubric.mimeType } }
      ]
    },
    config: {
      systemInstruction: "You are an Academic Quality Assurance officer at a UK Pharmacy School.",
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          confidenceScore: { type: Type.NUMBER },
          aiCompatibilityNotes: { type: Type.ARRAY, items: { type: Type.STRING } },
          pedagogicalRobustnessNotes: { type: Type.ARRAY, items: { type: Type.STRING } },
          suggestedActionPoints: { type: Type.ARRAY, items: { type: Type.STRING } },
          overallSummary: { type: Type.STRING }
        },
        required: ["confidenceScore", "aiCompatibilityNotes", "pedagogicalRobustnessNotes", "suggestedActionPoints", "overallSummary"]
      },
    }
  });

  return JSON.parse(response.text || "{}");
};
